/**
 * Die Notizen zu einem Kunden, Projekt, Termin oder Leed — dort, wo sie
 * entstehen. Oben eine Zeile für die schnelle Aufgabe (Enter, fertig), darunter
 * der Weg zum längeren Text. Gezeigt werden die Notizen beider Inhaber; wer
 * zuständig ist, steht an der Karte, wenn man es nicht selbst ist.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { fehlerText } from "@vimaana/ui";

import { useNotizAnlegen, useNotizen } from "../../hooks/useNotizen";
import type { NotizBezugTeil } from "../../lib/notizen";
import NotizErfassen from "./NotizErfassen";
import NotizKarte from "./NotizKarte";
import "../../styles/projekte.css";
import "../../styles/notizen.css";

type Props = {
  /** Worauf die Liste gefiltert ist — und was eine neue Notiz als Bezug bekommt. */
  bezug: NotizBezugTeil;
  titel?: string;
  platzhalter?: string;
  /** Ohne Überschrift, z. B. als Spalte „Als Nächstes" am Projekt. */
  ohneKopf?: boolean;
  /** Eingabezeile gleich fokussieren — wenn der Block eigens zum Schreiben geöffnet wird (Kunde → „Notizen"). */
  fokus?: boolean;
};

export default function NotizenBlock({ bezug, titel = "Aufgaben", platzhalter, ohneKopf = false, fokus = false }: Props) {
  const [mitFertigen, setMitFertigen] = useState(false);
  const [schreiben, setSchreiben] = useState(false);
  const [zeile, setZeile] = useState("");
  const liste = useNotizen(mitFertigen ? "alle" : "offen", "alle", bezug);
  const anlegen = useNotizAnlegen();
  // Wichtige zuerst (Flagge, 20.10.2026); sonst bleibt die Reihenfolge des Servers.
  const notizen = [...(liste.data || [])].sort((a, b) => Number(!!b.wichtig) - Number(!!a.wichtig));

  const aufgabeDazu = () => {
    const t = zeile.trim();
    if (!t) return;
    anlegen.mutate({ text: "", punkte: [t], bezug }, { onSuccess: () => setZeile("") });
  };

  return (
    <section className="nz-block" aria-label={titel}>
      {!ohneKopf && (
        <header className="nz-block-kopf">
          <h3>{titel}</h3>
          <Link className="nz-link" to="/aufgaben">Alle Aufgaben</Link>
        </header>
      )}

      {/* Bewusst kein <form>: der Block steht teils in fremden Formularen
          (Kundenkonto), und die Eingabetaste würde dort den Kunden speichern. */}
      <div className="nz-schnell">
        <input
          className="nz-feld"
          value={zeile}
          maxLength={300}
          onChange={(e) => setZeile(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aufgabeDazu(); } }}
          placeholder={platzhalter || "Was ist zu tun?"}
          aria-label="Neue Aufgabe"
          autoFocus={fokus}
        />
        <button type="button" className="vg-btn" disabled={anlegen.isPending || !zeile.trim()} onClick={aufgabeDazu}>Dazu</button>
      </div>
      {!schreiben ? (
        <button type="button" className="nz-still nz-block-mehr" onClick={() => setSchreiben(true)}>+ Längere Notiz</button>
      ) : (
        <NotizErfassen bezug={bezug} autoFocus onFertig={() => setSchreiben(false)} />
      )}
      {anlegen.error && <p className="nz-fehler" role="alert">{fehlerText(anlegen.error)}</p>}

      {liste.isError && <p className="nz-fehler">{fehlerText(liste.error)}</p>}
      {notizen.map((n) => <NotizKarte key={n.id} notiz={n} kompakt />)}

      <button type="button" className="nz-still nz-block-mehr" aria-pressed={mitFertigen} onClick={() => setMitFertigen((v) => !v)}>
        {mitFertigen ? "Erledigte ausblenden" : "Erledigte zeigen"}
      </button>
    </section>
  );
}
